/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EditFuelTransaction } from '../../../types/FuelTransaction';
import { Period } from '../../../context/PeriodProvider';
import { FuelTransactionClient } from '../../../services/FuelTransactionClient';
import { useContext } from 'react';
import { UserContext } from '../../../context/UserProvider';
import { useParams } from 'react-router-dom';
import { AddFuelTransaction as AddFuelTransactionType } from '../../../types/FuelTransaction';
import { buildQueryKey } from '../../../lib/utils';

export const FuelTransactionsQueryKey = 'fuelTransactions';
export const FuelTransactionsReplacementQueryKey = 'fuelTransactionsReplacement';

export const useFuelTransaction = () => {
    const queryClient = useQueryClient();
    const user = useContext(UserContext);
    const { id } = useParams();

    const invalidateQuery = () => {
        queryClient.invalidateQueries();
    };

    const addFuelTransaction = () => {
        return useMutation({
            mutationFn: (data: AddFuelTransactionType) => FuelTransactionClient({...user, param: id}).addFuelTransaction(data),
            onSuccess: () => invalidateQuery(),
        });
    };

    const getFuelTransactions = (period: Period) => {
        return useQuery({
            queryKey: [buildQueryKey(FuelTransactionsQueryKey, id), period],
            queryFn: () => FuelTransactionClient({ ...user, param: id }).getFuelTransactions(period),
        });
    };

    const getReplacementFuelTransactions = (period: Period) => {
        return useQuery({
            queryKey: [buildQueryKey(FuelTransactionsReplacementQueryKey, id), period],
            queryFn: () => FuelTransactionClient({ ...user, param: id }).getReplacementFuelTransactions(period),
        });
    };

    const editFuelTransaction = () => {
        return useMutation({
            mutationFn: (data: EditFuelTransaction) => FuelTransactionClient(user).editFuelTransaction(data),
            onSuccess: () => invalidateQuery(),
        });
    };

    const unMarkAsSubstituteCarUsage = () => {
        return useMutation({
            mutationFn: (id: string) => FuelTransactionClient(user).unMarkAsSubstituteCarUsage(id),
            onSuccess: () => {
                invalidateQuery()
            },
        });
    };
    const markAsSubstituteCarUsage = () => {
        return useMutation({
            mutationFn: (id: string) => FuelTransactionClient(user).markAsSubstituteCarUsage(id),
            onSuccess: () => {
                invalidateQuery()
            },
        });
    };

    const deleteFuelTransaction = () => {
        return useMutation({
            mutationFn: (id: string) => FuelTransactionClient(user).deleteFuelTransaction(id),
            onSuccess: () => invalidateQuery(),
        });
    };

    return {
        addFuelTransaction,
        getFuelTransactions,
        getReplacementFuelTransactions,
        editFuelTransaction,
        deleteFuelTransaction,
        invalidateFuelTransactionQuery: invalidateQuery,
        markAsSubstituteCarUsage,
        unMarkAsSubstituteCarUsage,
    };
};
